// v1.32: /settings 改造 — 3 tab 布局 (信息 / 偏好 / 安全)
// v1.28.3: 头像上传 (R2 multipart + UserMenu fallback)
// v1.32: 顶部用户卡 + Tabs primitive + 偏好占位 + 安全 tab 收纳改密码/头像
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Icon } from '../components/Icon';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { TabPanel, Tabs } from '../components/ui/Tabs';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

type SettingsTab = 'profile' | 'preferences' | 'security';

function getInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

function getColorFromName(name: string): string {
  // 跟 UserMenu 一致 — 6 柔和背景色
  const palette = ['bg-accent', 'bg-info', 'bg-good', 'bg-warn', 'bg-ink-2', 'bg-bad'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length] ?? 'bg-accent';
}

function SettingsPage() {
  const {
    user,
    updateProfile,
    updatePassword,
    uploadAvatar,
    availableCompanies,
    currentCompanyId,
    switchCompany,
  } = useAuthStore();

  // v1.32: tab 状态
  const [tab, setTab] = useState<SettingsTab>('profile');

  // === Profile (信息 tab) ===
  const [name, setName] = useState(user?.displayName ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSuccess, setNameSuccess] = useState(false);
  const [savingName, setSavingName] = useState(false);

  // === Security (安全 tab — 头像 + 改密码) ===
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatarUrl ?? null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarSuccess, setAvatarSuccess] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // === Preferences (偏好 tab — 占位, v1.32 不写后端) ===
  // 主题/语言/通知 等设置后端 schema 还没建, 先 UI-only (localStorage)
  const [themePref, setThemePref] = useState<'light' | 'dark' | 'auto'>(
    (typeof window !== 'undefined' &&
      (localStorage.getItem('theme') as 'light' | 'dark' | 'auto')) ||
      'light',
  );
  const [langPref, setLangPref] = useState<'zh' | 'ja' | 'en'>(
    (typeof window !== 'undefined' && (localStorage.getItem('lang') as 'zh' | 'ja' | 'en')) || 'zh',
  );
  const [notifyPref, setNotifyPref] = useState<boolean>(
    typeof window === 'undefined' ? true : localStorage.getItem('notify') !== 'false',
  );

  function handleAvatarPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setAvatarError('文件超过 5MB');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) {
      setAvatarError('仅支持 PNG / JPG / WebP');
      return;
    }
    setAvatarError(null);
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  }

  async function handleAvatarUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!avatarFile) {
      setAvatarError('请先选一张图片');
      return;
    }
    setAvatarError(null);
    setUploadingAvatar(true);
    try {
      const res = await uploadAvatar(avatarFile);
      setAvatarPreview(res.avatarUrl);
      setAvatarFile(null);
      setAvatarSuccess(true);
      setTimeout(() => setAvatarSuccess(false), 3000);
    } catch (err) {
      const e2 = err as { message?: string };
      setAvatarError(e2.message ?? '上传失败');
    } finally {
      setUploadingAvatar(false);
    }
  }

  if (!user) return null;

  async function handleSaveName(e: React.FormEvent) {
    e.preventDefault();
    setNameError(null);
    setNameSuccess(false);
    const cleaned = name.trim();
    if (cleaned.length < 1 || cleaned.length > 30) {
      setNameError('姓名需 1-30 字');
      return;
    }
    if (cleaned === user?.displayName) {
      setNameError('姓名未变化');
      return;
    }
    setSavingName(true);
    try {
      await updateProfile(cleaned);
      setNameSuccess(true);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch (err) {
      const e2 = err as { message?: string };
      setNameError(e2.message ?? '保存失败');
    } finally {
      setSavingName(false);
    }
  }

  async function handleSavePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwdError(null);
    setPwdSuccess(false);
    if (newPwd.length < 8) {
      setPwdError('新密码至少 8 位');
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdError('两次输入的新密码不一致');
      return;
    }
    if (newPwd === currentPwd) {
      setPwdError('新密码必须与当前密码不同');
      return;
    }
    setSavingPwd(true);
    try {
      await updatePassword(currentPwd, newPwd);
      setPwdSuccess(true);
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
      setTimeout(() => setPwdSuccess(false), 3000);
    } catch (err) {
      const e2 = err as { message?: string };
      setPwdError(e2.message ?? '修改失败');
    } finally {
      setSavingPwd(false);
    }
  }

  const colorClass = getColorFromName(user.displayName);
  const initial = getInitial(user.displayName);
  const currentCompany = availableCompanies.find((c) => c.id === currentCompanyId);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="text-xs text-ink-3">
        <Link to="/" className="hover:text-ink-2 transition-colors">
          ← 返回
        </Link>
      </div>

      {/* === 顶部: 当前用户卡 === */}
      <Card padding="md" className="animate-fade-up">
        <div className="flex items-center gap-4">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.displayName}
              className="w-16 h-16 rounded-full object-cover border border-line shrink-0"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div
              className={`w-16 h-16 rounded-full ${colorClass} text-white flex items-center justify-center text-xl font-semibold shrink-0`}
            >
              {initial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-semibold text-ink leading-tight truncate">
              {user.displayName}
            </h1>
            <div className="text-xs text-ink-3 truncate mt-0.5">{user.email}</div>
            <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-line text-ink-2">
                {user.role === 'owner' && '所有者 (owner)'}
                {user.role === 'sales' && '销售 (sales)'}
                {user.role === 'ops' && '运营 (ops)'}
                {user.role === 'finance' && '财务 (finance)'}
              </span>
              {currentCompany && (
                <span className="inline-flex items-center gap-1 text-ink-3">
                  <span>{currentCompany.flag}</span>
                  <span>{currentCompany.name}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* === Tabs === */}
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as SettingsTab)}
        variant="underline"
        ariaLabel="设置分区"
        items={[
          { value: 'profile', label: '信息', icon: 'user' },
          { value: 'preferences', label: '偏好', icon: 'sun-moon' },
          { value: 'security', label: '安全', icon: 'lock' },
        ]}
      />

      {/* === Tab 1: 信息 (profile) === */}
      <TabPanel value="profile" active={tab === 'profile'}>
        <div className="space-y-4">
          {/* 账号信息 (只读) */}
          <Card padding="md" className="animate-fade-up">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-accent rounded-sm" />
              账号信息
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Field label="邮箱">
                <div className="text-ink px-3 py-2 bg-surface-2 rounded border border-line truncate">
                  {user.email}
                </div>
              </Field>
              <Field label="角色">
                <div className="text-ink px-3 py-2 bg-surface-2 rounded border border-line">
                  {user.role === 'owner' && '所有者 (owner)'}
                  {user.role === 'sales' && '销售 (sales)'}
                  {user.role === 'ops' && '运营 (ops)'}
                  {user.role === 'finance' && '财务 (finance)'}
                </div>
              </Field>
            </div>
            <p className="text-xs text-ink-3 mt-3">
              邮箱与角色暂不支持修改。如需变更请联系管理员。
            </p>
          </Card>

          {/* 修改 displayName */}
          <Card padding="md" className="animate-fade-up" style={{ animationDelay: '30ms' }}>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-accent rounded-sm" />
              显示名
            </h2>
            <form onSubmit={handleSaveName} className="space-y-3">
              <Field label="姓名 (1-30 字)">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={30}
                  className="w-full px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15 transition-colors"
                  required
                  aria-label="显示名称"
                />
              </Field>
              {nameError && <Alert variant="error">{nameError}</Alert>}
              {nameSuccess && <Alert variant="success">已保存</Alert>}
              <div className="flex justify-end">
                <Button type="submit" variant="primary" size="sm" loading={savingName}>
                  {savingName ? '保存中…' : '保存'}
                </Button>
              </div>
            </form>
          </Card>

          {/* 公司切换 (多公司时) */}
          {availableCompanies.length > 1 && (
            <Card padding="md" className="animate-fade-up" style={{ animationDelay: '60ms' }}>
              <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-accent rounded-sm" />
                切换公司
              </h2>
              <div className="space-y-1.5">
                {availableCompanies.map((c) => {
                  const isCurrent = c.id === currentCompanyId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => !isCurrent && switchCompany(c.id)}
                      disabled={isCurrent}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm rounded border transition-colors ${
                        isCurrent
                          ? 'border-accent bg-accent-soft text-ink'
                          : 'border-line hover:bg-surface-2 text-ink-2'
                      }`}
                    >
                      <span className="text-base">{c.flag}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{c.name}</div>
                        <div className="text-[10px] text-ink-3">{c.code}</div>
                      </div>
                      {isCurrent && <Icon name="check" size="sm" className="text-accent" />}
                    </button>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      </TabPanel>

      {/* === Tab 2: 偏好 (preferences) — UI 占位 === */}
      <TabPanel value="preferences" active={tab === 'preferences'}>
        <Card padding="md" className="animate-fade-up">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-accent rounded-sm" />
            外观
          </h2>
          <Field label="主题">
            <div className="grid grid-cols-3 gap-2">
              {(['light', 'dark', 'auto'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setThemePref(t);
                    try {
                      localStorage.setItem('theme', t);
                    } catch {}
                  }}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    themePref === t
                      ? 'border-accent bg-accent-soft text-ink font-medium'
                      : 'border-line text-ink-2 hover:bg-surface-2'
                  }`}
                >
                  {t === 'light' ? '☀️ 浅色' : t === 'dark' ? '🌙 深色' : '⚙️ 跟随系统'}
                </button>
              ))}
            </div>
          </Field>
          <p className="text-xs text-ink-3 mt-3">
            主题 (UI-only, v1.32 暂存 localStorage; v1.33 同步到后端 user preference)
          </p>
        </Card>

        <Card padding="md" className="animate-fade-up mt-4" style={{ animationDelay: '30ms' }}>
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-accent rounded-sm" />
            语言
          </h2>
          <Field label="界面语言">
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  { v: 'zh', l: '中文' },
                  { v: 'ja', l: '日本語' },
                  { v: 'en', l: 'English' },
                ] as const
              ).map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => {
                    setLangPref(o.v);
                    try {
                      localStorage.setItem('lang', o.v);
                    } catch {}
                  }}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    langPref === o.v
                      ? 'border-accent bg-accent-soft text-ink font-medium'
                      : 'border-line text-ink-2 hover:bg-surface-2'
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </Field>
        </Card>

        <Card padding="md" className="animate-fade-up mt-4" style={{ animationDelay: '60ms' }}>
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-accent rounded-sm" />
            通知
          </h2>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={notifyPref}
              onChange={(e) => {
                setNotifyPref(e.target.checked);
                try {
                  localStorage.setItem('notify', String(e.target.checked));
                } catch {}
              }}
              className="rounded border-line text-accent focus:ring-accent"
            />
            <span>接收节日邮件 / 应收超期 提醒</span>
          </label>
        </Card>
      </TabPanel>

      {/* === Tab 3: 安全 (security) === */}
      <TabPanel value="security" active={tab === 'security'}>
        <div className="space-y-4">
          {/* 头像 */}
          <Card padding="md" className="animate-fade-up">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-accent rounded-sm" />
              头像
            </h2>
            <form onSubmit={handleAvatarUpload} className="flex items-start gap-4">
              <div className="shrink-0">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="头像预览"
                    className="w-20 h-20 rounded-full object-cover border border-line"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-surface-2 border border-line flex items-center justify-center text-ink-3 text-xs">
                    暂无
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleAvatarPick}
                  aria-label="选择头像"
                  className="block w-full text-sm text-ink file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-line file:bg-surface file:text-sm file:font-medium hover:file:bg-surface-2"
                />
                <p className="text-xs text-ink-3">支持 PNG / JPG / WebP, 最大 5MB</p>
                {avatarError && <Alert variant="error">{avatarError}</Alert>}
                {avatarSuccess && <Alert variant="success">头像已更新</Alert>}
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!avatarFile || uploadingAvatar}
                  loading={uploadingAvatar}
                >
                  {uploadingAvatar ? '上传中…' : '上传头像'}
                </Button>
              </div>
            </form>
          </Card>

          {/* 改密码 */}
          <Card padding="md" className="animate-fade-up" style={{ animationDelay: '30ms' }}>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-accent rounded-sm" />
              修改密码
            </h2>
            <form onSubmit={handleSavePassword} className="space-y-3">
              <Field label="当前密码">
                <input
                  type="password"
                  value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)}
                  className="w-full px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15 transition-colors"
                  required
                  autoComplete="current-password"
                  aria-label="当前密码"
                />
              </Field>
              <Field label="新密码 (至少 8 位)">
                <input
                  type="password"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  minLength={8}
                  className="w-full px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15 transition-colors"
                  required
                  autoComplete="new-password"
                  aria-label="新密码"
                />
              </Field>
              <Field label="确认新密码">
                <input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  minLength={8}
                  className="w-full px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15 transition-colors"
                  required
                  autoComplete="new-password"
                  aria-label="确认新密码"
                />
              </Field>
              {pwdError && <Alert variant="error">{pwdError}</Alert>}
              {pwdSuccess && <Alert variant="success">密码已修改</Alert>}
              <div className="flex justify-end">
                <Button type="submit" variant="primary" size="sm" loading={savingPwd}>
                  {savingPwd ? '修改中…' : '修改密码'}
                </Button>
              </div>
            </form>
          </Card>

          {/* 登录日志 (占位) */}
          <Card padding="md" className="animate-fade-up" style={{ animationDelay: '60ms' }}>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-accent rounded-sm" />
              登录日志
            </h2>
            <p className="text-xs text-ink-3">
              最近登录记录将在 v1.33 接入 session 表后提供. 现阶段请通过 UserMenu
              的"登出"按钮切换设备.
            </p>
          </Card>

          {/* API Token (占位) */}
          <Card padding="md" className="animate-fade-up" style={{ animationDelay: '90ms' }}>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-accent rounded-sm" />
              API Token
            </h2>
            <p className="text-xs text-ink-3">
              编程访问 Trade OS 的 API token 将在 v1.34 接入 RBAC 后提供. 现请使用 session cookie.
            </p>
          </Card>

          {/* Webhook 配置 — 留在这里因为是"安全"相关 */}
          <Card padding="md" className="animate-fade-up" style={{ animationDelay: '120ms' }}>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-accent rounded-sm" />
              官网表单 Webhook (v1.13)
            </h2>
            <p className="text-xs text-ink-3 mb-4">
              toei-sosan.com 联系表单的提交会通过 Web3Forms 同步落到 Trade OS, 用于客户背调 / 跟单。
            </p>

            <div className="space-y-3 text-sm">
              <Field label="Webhook URL (在 Web3Forms dashboard 配这个)">
                <div className="flex items-center gap-1">
                  <code className="flex-1 text-xs px-3 py-2 bg-surface-2 rounded border border-line font-mono break-all">
                    {`${typeof window !== 'undefined' ? window.location.origin : 'https://app.maclog.cn'}/api/inquiries/webhook/web3forms`}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      const url = `${window.location.origin}/api/inquiries/webhook/web3forms`;
                      navigator.clipboard.writeText(url);
                    }}
                    className="shrink-0 px-2 py-2 border border-line rounded text-xs hover:bg-surface-2 inline-flex items-center gap-1"
                    title="复制 webhook URL"
                  >
                    <Icon name="clipboard-list" size="xs" strokeWidth={2} />
                    复制
                  </button>
                </div>
              </Field>
            </div>
          </Card>
        </div>
      </TabPanel>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="block">
      <span className="text-xs text-ink-2 mb-1.5 block">{label}</span>
      {children}
    </div>
  );
}
